<template>
  <div class="d-flex justify-center">
    <v-expand-x-transition>
      <div
        v-show="showInspirations"
        class="article-inspiration"
      >
        <ArticleContentInspirations
          v-for="tag of ['h', 'p']"
          :key="part + tag"
          :inspirations="inspirations"
          :loading-inspirations="loadingInspirations"
          :part="part + tag"
          :words-to-highlight="wordsToHighlight"
        />
      </div>
    </v-expand-x-transition>
    <div
      :id="part"
      class="article-content"
    >
      <transition-group
        v-if="localBlocks.length === 0"
        name="card-list"
        mode="out-in"
      >
        <v-btn
          v-if="!$store.getters.articleAiLoading.loadingRatioContent"
          key="addFirstPartButton"
          class="multi-line-button mt-12"
          outlined
          block
          color="primary"
          :disabled="disabled"
          @click="addSubpart(0)"
        >
          <v-icon class="mr-2">
            playlist_add
          </v-icon>
          {{ $t('article.addSubpart', [$t('article.label.' + part + 'h')]) }}
        </v-btn>
      </transition-group>

      <draggable
        v-else
        v-model="localBlocks"
        :group="dragGroup"
        :animation="0"
        :disabled="disabled || part === 'h1'"
        handle=".drag-handle-btn"
        :filter="dragFilter"
        :prevent-on-filter="true"
        :force-fallback="true"
        :fallback-on-body="true"
        :fallback-tolerance="0"
        :fallback-offset="{ x: 0, y: 0 }"
        :scroll="true"
        :scroll-sensitivity="80"
        :scroll-speed="12"
        ghost-class="drag-ghost-placeholder"
        fallback-class="drag-fallback-clone"
        @start="onDragStart"
        @end="onDragEnd"
        @change="onBlocksChange"
      >
        <div
          v-for="({uid}, index) of localBlocks"
          :key="uid"
          class="card-list-item mt-14 block-draggable-wrapper"
          :class="{ 'block-dragging': isDragging }"
        >
          <div
            v-if="part !== 'h1'"
            class="drag-handle-btn"
            :title="$t('article.dragBlock')"
          >
            <v-icon color="grey darken-1">drag_indicator</v-icon>
          </div>
          <ArticleContentPartEditor
            :part="part"
            :index="storeIndex(uid)"
            :words-to-highlight="wordsToHighlight"
            :show-inspirations="showInspirations"
            :has-inspirations="hasInspirations"
            :disabled="disabled"
            @toggle-inspirations="toggleInspirations"
            @remove="onSubPartRemove(storeIndex(uid))"
          >
            <template #paragraphToolbarAppend>
              <VerticalDivider />
              <v-chip
                v-if="part !== 'h1' && !$store.getters.articleAiLoading.loadingRatioContent"
                small
                :disabled="$store.getters.articleAiLoading.loadingRatioContent > 0"
                outlined
                class="chip-text-primary"
                @click="addSubpart(index)"
              >
                <v-icon class="mr-2">
                  playlist_add
                </v-icon>
                {{ $t('article.addAnotherSubpart', [$t('article.label.' + part + 'h')]) }}
              </v-chip>
            </template>
          </ArticleContentPartEditor>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import {ArticleInspirations, ArticlePartContent} from "@/model/Article";
    import ArticleContentPartEditor from "@/components/article/ArticleContentPartEditor.vue";
    import ArticleContentInspirations from "@/components/article/ArticleContentInspirations.vue";
    import VerticalDivider from "@/components/VerticalDivider.vue";
    import EventsListenerMixin from "@/mixins/EventsListenerMixin";
    import draggable from 'vuedraggable';

    function teardownDragPreview(state: {
        dragPreviewMove: ((e: PointerEvent) => void) | null,
        dragPreviewEl: HTMLElement | null,
    }): void {
        if (state.dragPreviewMove) {
            window.removeEventListener('pointermove', state.dragPreviewMove);
            state.dragPreviewMove = null;
        }
        if (state.dragPreviewEl) {
            state.dragPreviewEl.remove();
            state.dragPreviewEl = null;
        }
    }

    export default Vue.extend({
        name: 'ArticleContentPartEditors',
        components: {VerticalDivider, ArticleContentInspirations, ArticleContentPartEditor, draggable},
        mixins: [EventsListenerMixin],
        props: {
            part: {
                type: String,
                default: () => ''
            },
            wordsToHighlight: {
                type: Object as PropType<{ [key: string]: string[] }>,
                default: () => ({})
            },
            inspirations: {
                type: Object as PropType<ArticleInspirations>,
                default: () => ({})
            },
            loadingInspirations: {
                type: Boolean,
                default: false
            },
            disabled: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            showInspirations: false,
            dialog: false,
            dialogIndex: 0,
            localBlocks: [] as ArticlePartContent[],
            isDragging: false,
            dragPreviewEl: null as HTMLElement | null,
            dragPreviewMove: null as ((e: PointerEvent) => void) | null,
        }),
        computed: {
            events: function () {
                return {
                    [this.part + '-inspiration-expand']: this.expandInspirations,
                };
            },
            articleId: function (): string {
                return this.$route.params.articleId as string;
            },
            value: function (): ArticlePartContent[] {
                return this.$store.getters.article[this.part];
            },
            hasInspirations: function (): boolean {
                return Object.values(this.inspirations)
                    .some(site => {
                        return (site as any)[this.part + 'h']?.length > 0
                            || (site as any)[this.part + 'p']?.length > 0;
                    });
            },
            dragGroup: function () {
                if (this.part === 'h1') {
                    return { name: 'articleBlocks', pull: false, put: false };
                }
                return { name: 'articleBlocks', pull: true, put: true };
            },
            // Selectors that should NOT start a drag — editor content, buttons, chips
            dragFilter: function (): string {
                return '.ck-editor__editable, .ck-toolbar, .v-btn, .v-btn__content, .v-chip, .v-chip__content, input, textarea';
            },
        },
        watch: {
            // Keep localBlocks in sync with the store, but only when NOT dragging.
            // During a drag, SortableJS owns the array; we write to Vuex only on @end.
            value: {
                immediate: true,
                handler(this: any, newVal: ArticlePartContent[]) {
                    if (!this.isDragging) {
                        this.localBlocks = [...newVal];
                    }
                },
            },
        },
        beforeDestroy() {
            teardownDragPreview(this as { dragPreviewMove: ((e: PointerEvent) => void) | null, dragPreviewEl: HTMLElement | null });
        },
        methods: {
            addSubpart: function (index: number): void {
                this.$store.commit('insertArticleContentSubPart', {
                    contentPart: this.part,
                    index: index + 1,
                    aiLoading: false,
                    keepContent: false
                });
            },
            onSubPartRemove: function (index: number) {
                this.$store.commit('removeArticleContentSubPart', {
                    contentPart: this.part,
                    index: index,
                });
            },
            toggleInspirations: function () {
                this.showInspirations = !this.showInspirations;
            },
            expandInspirations: function () {
                this.showInspirations = true;
            },
            // Map a block UID to its current index in the Vuex store.
            // Used so ArticleContentPartEditor always reads the right slot
            // even when localBlocks is temporarily reordered during drag.
            storeIndex(uid: number): number {
                const storeArr: ArticlePartContent[] = (this as any).value;
                const idx = storeArr.findIndex((b: ArticlePartContent) => b.uid === uid);
                return idx >= 0 ? idx : 0;
            },
            onDragStart(evt: { item?: HTMLElement, clone?: HTMLElement, originalEvent?: MouseEvent }): void {
                this.isDragging = true;
                if (evt.clone) {
                    evt.clone.style.setProperty('display', 'none', 'important');
                    evt.clone.style.setProperty('opacity', '0', 'important');
                }

                const heading = evt.item?.querySelector('.ck-content');
                const text = (heading?.textContent || '').replace(/\s+/g, ' ').trim() || '…';
                const preview = document.createElement('div');
                preview.className = 'article-drag-preview'
                    + (this.$vuetify.theme.dark ? ' article-drag-preview--dark' : '');
                preview.textContent = text;
                document.body.appendChild(preview);
                this.dragPreviewEl = preview;

                const move = (e: PointerEvent | MouseEvent) => {
                    preview.style.transform = `translate3d(${e.clientX + 14}px, ${e.clientY + 10}px, 0)`;
                };
                this.dragPreviewMove = move as (e: PointerEvent) => void;
                const origin = evt.originalEvent;
                if (origin && typeof origin.clientX === 'number') {
                    move(origin);
                }
                window.addEventListener('pointermove', this.dragPreviewMove, {passive: true});
            },
            onDragEnd(): void {
                teardownDragPreview(this as { dragPreviewMove: ((e: PointerEvent) => void) | null, dragPreviewEl: HTMLElement | null });
                this.isDragging = false;
                this.$store.commit('setArticleContentArray', {
                    contentPart: this.part,
                    newArray: [...this.localBlocks],
                });
            },
            // A block arrived from another list — commit the updated list to Vuex.
            // (@end fires on the source; @change/added fires on the target.)
            onBlocksChange(event: any): void {
                if (event.added) {
                    this.$store.commit('setArticleContentArray', {
                        contentPart: this.part,
                        newArray: [...this.localBlocks],
                    });
                }
            },
        }
    });
</script>

<style lang="sass" scoped>
.drag-handle-btn
  position: absolute
  left: -30px
  top: 10px
  z-index: 3
  display: flex
  align-items: center
  justify-content: center
  width: 28px
  height: 36px
  cursor: grab
  opacity: 0.45
  transition: opacity 0.15s ease, background 0.15s ease
  border-radius: 6px
  touch-action: none

  &:hover
    opacity: 1
    background: rgba(0, 0, 0, 0.06)

  &:active
    cursor: grabbing

.block-draggable-wrapper
  position: relative
  cursor: default

  &:hover
    .drag-handle-btn
      opacity: 0.75

.block-dragging .drag-handle-btn
  cursor: grabbing
  opacity: 1
</style>

<style lang="sass">
.drag-fallback-clone
  display: none !important
  opacity: 0 !important
  pointer-events: none !important

.article-drag-preview
  position: fixed
  left: 0
  top: 0
  z-index: 10000
  pointer-events: none
  background: #ffffff
  color: rgba(0, 0, 0, 0.87)
  border: 1px solid rgba(58, 53, 65, 0.12)
  border-radius: 12px
  box-shadow: 0 10px 28px rgba(58, 53, 65, 0.22)
  padding: 12px 16px
  font-family: Roboto, sans-serif
  font-size: 0.95rem
  font-weight: 600
  line-height: 1.3
  max-width: 420px
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
  opacity: 1
  will-change: transform

.article-drag-preview--dark
  background: #2f3349
  color: #fff
  border-color: rgba(255, 255, 255, 0.14)
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45)

.drag-ghost-placeholder
  min-height: 72px !important
  height: 72px !important
  overflow: hidden !important
  border-radius: 12px !important
  border: 2px dashed rgba(8, 135, 220, 0.45) !important
  background: rgba(8, 135, 220, 0.06) !important
  box-shadow: none !important

  > *
    visibility: hidden !important
    height: 0 !important
    overflow: hidden !important
    margin: 0 !important
    padding: 0 !important
</style>
